import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PmoShell from "./PmoShell";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Workflow, ArrowUpRight, Archive } from "lucide-react";
import { ProcessAiBuilder } from "@/components/ProcessAiBuilder";

export default function PmoProcess() {
  const [procs, setProcs] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/pmo/processes").then(({ data }) => setProcs(data)).catch(() => setProcs([]));
  }, []);

  const shown = useMemo(() => {
    if (!procs) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return procs;
    return procs.filter((p) => `${p.name} ${p.description} ${p.project_name}`.toLowerCase().includes(needle));
  }, [procs, q]);

  return (
    <PmoShell
      title="Process"
      subtitle="Every process flow documented across your projects — the organization's operating playbook."
      testid="pmo-process-page"
      actions={(
        <div className="flex flex-wrap items-center gap-2">
          <ProcessAiBuilder onCreated={(p) => setProcs((prev) => (prev ? [p, ...prev] : [p]))} />
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search processes…" className="pl-9" data-testid="pmo-process-search" />
          </div>
        </div>
      )}
    >
      {procs === null && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
      {procs !== null && procs.length === 0 && (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="pmo-process-empty">
          <Workflow className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">No processes documented yet — build one from any project's Process tab and it'll appear here.</p>
        </div>
      )}
      {procs !== null && procs.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shown.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No processes match "{q}".</p>}
          {shown.map((p) => (
            <button key={p.id} onClick={() => nav(`/projects/${p.project_id}?tab=process`)}
              className="text-left rounded-xl border border-border bg-white p-4 hover:border-primary/40 hover:shadow-sm transition-all group"
              data-testid={`pmo-process-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Workflow className="h-4 w-4 text-primary shrink-0" />
                  <p className="font-display font-bold truncate">{p.name}</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </div>
              {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
              <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1.5">
                {p.project_archived && <Archive className="h-3 w-3" />} {p.project_name} · {p.steps} steps
                {p.updated_at ? ` · updated ${new Date(p.updated_at).toLocaleDateString()}` : ""}
              </p>
            </button>
          ))}
        </div>
      )}
    </PmoShell>
  );
}
